package io.github.retar.portfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initSyncing_reading_positionsPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("syncing_reading_positions.md", FrontMatterElement.Builder { addScalar("title", "Never lose someone's place: syncing reading positions across devices and formats"); addScalar("description", "How Parrot syncs where you are in a book: local-first writes, compare-and-set revisions on the server, when to settle a conflict and when to ask, and moving a position between an ebook and its audiobook."); addScalar("date", "2026-10-13"); addScalar("cover", "/articles/syncing_positions.jpeg"); }))
}

@Page("/blog/syncing-reading-positions")
@Layout(".pages.MarkdownPage")
@Composable
fun Syncing_reading_positionsPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("never-lose-someone-s-place-syncing-reading-positions-across-devices-and-formats") }) {
        org.jetbrains.compose.web.dom.Text("Never lose someone's place: syncing reading positions across devices and formats")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A reading app has one job that matters more than any feature: when you open a book, it should be where you left it. Parrot runs on phones, tablets and e-ink readers, and the same book can exist as an EPUB, a read-along EPUB and an audiobook on a different server. So \"where you left it\" turns into a distributed systems problem with several devices, several servers, unreliable clocks and a person who reads offline on a train.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This note is about how Parrot syncs reading positions, and the rules I ended up with after getting it wrong a few times.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-rule-everything-else-follows") }) {
        org.jetbrains.compose.web.dom.Text("The rule everything else follows")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Early on I wrote down the thing I was most tempted to do, so I wouldn't do it:")
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Never resolve by maximum percentage. Never replace the original reading time with upload/retry time. Device wall clocks and server arrival order alone are not reliable proof of causality.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("\"Furthest wins\" sounds safe until you open a book on an old tablet where you once skimmed ahead to the last chapter. Then every device jumps to the end. \"Newest upload wins\" sounds safe until a phone that was offline for a day uploads a stale position and overwrites the one you just made. Both rules are simple, and both lose someone's place.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("local-first") }) {
        org.jetbrains.compose.web.dom.Text("Local first")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every position is written to the device's own database first, in a SQLDelight table, before any network call. The commit is the moment it's safe. Syncing happens later, from an outbox.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The position row carries more than a percentage. Three fields do most of the work:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            io.github.retar.portfolio.components.widgets.InlineCode("""observed_at""")
            org.jetbrains.compose.web.dom.Text(": the schema comment says it best, \"When the reading happened, not when it was saved.\" An upload that retries for an hour keeps the original time.")
        }
        org.jetbrains.compose.web.dom.Li {
            io.github.retar.portfolio.components.widgets.InlineCode("""origin""")
            org.jetbrains.compose.web.dom.Text(": one of ")
            io.github.retar.portfolio.components.widgets.InlineCode("""user""")
            org.jetbrains.compose.web.dom.Text(", ")
            io.github.retar.portfolio.components.widgets.InlineCode("""restore""")
            org.jetbrains.compose.web.dom.Text(", ")
            io.github.retar.portfolio.components.widgets.InlineCode("""remote""")
            org.jetbrains.compose.web.dom.Text(", ")
            io.github.retar.portfolio.components.widgets.InlineCode("""linked_copy""")
            org.jetbrains.compose.web.dom.Text(" or ")
            io.github.retar.portfolio.components.widgets.InlineCode("""manual""")
            org.jetbrains.compose.web.dom.Text(". Only user, remote and manual positions count as real reading. A position restored from a backup or copied from another format of the book is useful, but it's not evidence that you read there.")
        }
        org.jetbrains.compose.web.dom.Li {
            io.github.retar.portfolio.components.widgets.InlineCode("""text_anchor""")
            org.jetbrains.compose.web.dom.Text(": about 20 words before and 30 after the position. The comment on that column is \"Never sent to a server.\" It's the reader's text, and it stays on the device.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Each change also goes into a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""sync_outbox""")
        org.jetbrains.compose.web.dom.Text(" row with a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""mutation_id""")
        org.jetbrains.compose.web.dom.Text(" and the server revision it was based on.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-server-compare-and-set-not-last-writer-wins") }) {
        org.jetbrains.compose.web.dom.Text("The server: compare-and-set, not last writer wins")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On Parrot's own server (Supabase, Postgres), every book's position has a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""revision""")
        org.jetbrains.compose.web.dom.Text(". A write says which revision it was based on, and the server function only accepts it if that's still the current one:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""select revision, payload into current_revision_value, current_payload
from public.reading_positions where cloud_book_id = cloud_book_id_value
for update;
if current_revision_value is not null
    and (base_revision_value is null or base_revision_value <> current_revision_value)
then
    result := jsonb_build_object('mutation_id', mutation_id_value,
        'status', 'conflict', 'cloud_book_id', cloud_book_id_value,
        'revision', current_revision_value, 'payload', current_payload);
else
    new_revision_value := coalesce(current_revision_value, 0) + 1;
""", lang = "sql")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A rejected write gets the current position back, so the client can decide what to do without another round trip.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Writes are also idempotent. The server stores its first answer for each ")
        io.github.retar.portfolio.components.widgets.InlineCode("""mutation_id""")
        org.jetbrains.compose.web.dom.Text(" and replays it if the same id comes again, so a retry after a dropped connection can't apply a change twice. That has a consequence I learned the hard way: an answer is final for its id. A write that was rejected once and then rebased onto the new revision must go out under a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("new")
        }
        org.jetbrains.compose.web.dom.Text(" mutation id, or the server just replays the old rejection.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("when-to-settle-when-to-ask") }) {
        org.jetbrains.compose.web.dom.Text("When to settle, when to ask")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A conflict happens when the device and the server disagree about where you are. Parrot has three possible responses, decided by one small function:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""if (kotlin.math.abs(localPercent - remotePercent) * 100.0 < POSITION_PERCENT_TOLERANCE) {
    return ConflictDecision.Silence
}
val localMillis = localPosition.epochObservedAtMillis() ?: return ConflictDecision.Ask
val remoteMillis = remotePosition.epochObservedAtMillis() ?: return ConflictDecision.Ask
...
return when {
    localNewer && localFurther -> ConflictDecision.KeepThis(remotePosition)
    remoteNewer && remoteFurther -> ConflictDecision.MoveToOther(localPosition)
    else -> ConflictDecision.Ask
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Less than 1% apart:")
            }
            org.jetbrains.compose.web.dom.Text(" say nothing.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("One side is both newer and further:")
            }
            org.jetbrains.compose.web.dom.Text(" that's the normal case of reading on one device and then picking up another. Parrot settles it and shows a bar with Undo, holding on to the position it didn't pick.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Anything else:")
            }
            org.jetbrains.compose.web.dom.Text(" newer but behind, missing a timestamp, missing a percentage. Parrot asks.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Asking is the fallback on purpose. If you read further on your phone yesterday but went back to re-read a chapter on your tablet today, the app can't know which one you meant. You do. One tap is cheap; losing your place isn't.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-device-that-conflicted-with-itself") }) {
        org.jetbrains.compose.web.dom.Text("The device that conflicted with itself")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The best bug in this area: a phone kept asking you to choose between two positions, and both had come from that phone.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The cause was the outbox. When the server accepted write A, the client moved the base revision forward only for the row it had just sent. Write B, already queued behind it, still carried the old base revision, so the server rejected it as a conflict, against A, from the same device.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The fix has three parts:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("When the server reports a conflict whose current position came from this same device, and the queued write is not older, it's not a conflict. The client rebases onto the server's revision and resends in the same push, under a new mutation id.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("An acknowledgement now moves the base revision of every queued write behind it.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A one-time repair step reopens rows that were already stuck as conflicts.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A follow-up bug hid behind it. Some of those stored conflicts were less than 1% apart, so the decision function chose Silence, and the prompt never appeared. The conflict was preserved but never shown. Now any preserved conflict forces Ask.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("signing-in-shouldn-t-move-you-backwards") }) {
        org.jetbrains.compose.web.dom.Text("Signing in shouldn't move you backwards")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Another bug: signing in pulled the server's position and replaced a newer one on the device. One report had a book drop from 9.6% to 4.8%. The pull only checked whether the device had unsent outbox rows for that account, and when it didn't, the server value won. Nothing compared reading times.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Now there's a guard: a remote position never replaces the person's own newer reading. The sync engine compares reading times here, but only for positions that were real reading on this device, never for restored or copied ones. I could fix the logic, but I couldn't prove what triggered it in the original report. I've written that down instead of pretending otherwise.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-book-many-copies") }) {
        org.jetbrains.compose.web.dom.Text("One book, many copies")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The harder half is moving a position between formats. If you listen to chapter 4 of the audiobook in the car, the ebook should offer to open at the same sentence. Parrot tries a series of strategies and stops at the first one that works:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""val translated = sameFile(request)
    ?: textAnchor(request)
    ?: smil(request)
    ?: trackToText(request).takeIf { _ -> !target.isAudio }
    ?: proportional(request)
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Same file.")
            }
            org.jetbrains.compose.web.dom.Text(" The content hash matches, so the position copies over as is. Confidence: exact.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Text anchor.")
            }
            org.jetbrains.compose.web.dom.Text(" Search the target for the words around the position. First an exact search on the normalized text, with the words before the position breaking ties; then a fuzzy Levenshtein match within 10% of where the percentage says it should be, which needs a score of at least 92. Confidence: high.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Media overlays.")
            }
            org.jetbrains.compose.web.dom.Text(" A read-along book already maps every sentence to a time in its audio. That map works as a bridge between text and audio. An audiobook from another server can use it only if its total duration matches within 1%. Confidence: high.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Percentage.")
            }
            org.jetbrains.compose.web.dom.Text(" The fallback, using chapters when both copies have the same number. Confidence: approximate.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Confidence decides what happens next. Only exact and high results from real reading are copied to other formats automatically, and they're written with origin ")
        io.github.retar.portfolio.components.widgets.InlineCode("""linked_copy""")
        org.jetbrains.compose.web.dom.Text(", so they never count as reading evidence. Approximate matches are shown in the positions panel unticked. Guards refuse any match that collapses to the very start or end of the book, which is what a failed match usually looks like.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Switching between copies always asks first. The reasoning I wrote down was \"A wrong jump loses the reader's place. A prompt costs one tap.\" The clock rule follows from that too: another copy has to be newer by 60 seconds before Parrot even offers to move. A prompt is harmless when it's wrong; an override isn't.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("small-rules-that-held-up") }) {
        org.jetbrains.compose.web.dom.Text("Small rules that held up")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Device names are labels only.")
            }
            org.jetbrains.compose.web.dom.Text(" Positions show which device they came from, but the name never takes part in comparisons. A rename must not create a conflict.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("The device is recorded when you read, not when it uploads")
            }
            org.jetbrains.compose.web.dom.Text(", so a delayed upload can't be credited to the wrong device.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Choosing \"use the server's position\" doesn't post anything back.")
            }
            org.jetbrains.compose.web.dom.Text(" \"Keep this one\" queues exactly one write on the revision you were shown. If someone read further in the meantime, the choice fails instead of overwriting newer reading.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Each backend gets honest limits.")
            }
            org.jetbrains.compose.web.dom.Text(" Parrot's own server does compare-and-set. Storyteller rejects stale writes by timestamp. Audiobookshelf has no conditional writes at all, so there the client can't guarantee ordering, and I say so in the docs instead of claiming it can.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-s-not-done") }) {
        org.jetbrains.compose.web.dom.Text("What's not done")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The app says \"saved, syncing\" but not yet \"synced\", because it doesn't show the server's acknowledgement. Two writes racing to create a position for a book that has none yet aren't locked against each other, since ")
        io.github.retar.portfolio.components.widgets.InlineCode("""FOR UPDATE""")
        org.jetbrains.compose.web.dom.Text(" can't lock a row that doesn't exist. And a newer local position kept by the sign-in guard is only uploaded back for Parrot's own server, not for Storyteller or Audiobookshelf.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The lesson underneath all of it: a sync design is a list of things you won't do. Don't trust the clock alone, don't trust the biggest number, don't let a retry change history, and when you really can't tell, ask the person who knows.")
    }
}
